import mockStatementsData from "../data/mockData/statementsData";
import { getSubUserRows } from "./subUserDemoHelpers";
import {
  getGeneratedSubuserStatements,
  getSubuserReportsMap,
  getSubuserStatementOverridesMap,
  setGeneratedSubuserStatements,
  upsertSubuserReport,
} from "./subuserAccountingSessionStore";

export const USER_REPORT_ITEM_ID_OFFSET = 700000;
export const SUBUSER_STATEMENT_ID_OFFSET = 800000;

/** Parent statements that start with subuser accounting already completed in demo. */
const SEEDED_PARENT_STATEMENT_IDS = [995, 994, 993];

function ensureSeededSubuserAccounting() {
  if (getGeneratedSubuserStatements().length > 0) {
    return;
  }

  const allGenerated = [];

  SEEDED_PARENT_STATEMENT_IDS.forEach(statementId => {
    const parent = mockStatementsData.find(
      row =>
        String(row.id) === String(statementId) &&
        Number(row.sts_kind) === 2,
    );
    if (!parent) {
      return;
    }

    const parentReportId = buildUserReportItemId(parent.id);
    const generated = generateSubuserStatementsFromParent(parent, parentReportId);
    if (!generated.length) {
      return;
    }

    upsertSubuserReport(parent.id, {
      user_report_item_id: parentReportId,
      subuser_accounting_status: "completed",
    });
    allGenerated.push(...generated);
  });

  if (allGenerated.length > 0) {
    setGeneratedSubuserStatements(allGenerated);
  }
}

function seededRandom(seed) {
  const x = Math.sin(Number(seed) || 0) * 10000;
  return x - Math.floor(x);
}

export function buildUserReportItemId(statementId) {
  return USER_REPORT_ITEM_ID_OFFSET + Number(statementId);
}

export function buildSubuserStatementId(parentStatementId, index) {
  return SUBUSER_STATEMENT_ID_OFFSET + Number(parentStatementId) * 100 + index;
}

export function getActiveSubUsers() {
  return getSubUserRows().filter(user => user.is_active && !user.is_blacklisted);
}

function pickSubusersForSplit(activeSubUsers, seed) {
  const sorted = [...activeSubUsers].sort(
    (a, b) => Number(a.id) - Number(b.id),
  );
  const maxCount = sorted.length;
  const targetCount = Math.max(1, 1 + (seed % maxCount));
  const picked = [];

  for (let i = 0; i < sorted.length && picked.length < targetCount; i += 1) {
    const idx = (seed + i * 7) % sorted.length;
    const candidate = sorted[idx];
    if (!picked.some(user => user.id === candidate.id)) {
      picked.push(candidate);
    }
  }

  sorted.forEach(user => {
    if (picked.length >= targetCount) {
      return;
    }
    if (!picked.some(entry => entry.id === user.id)) {
      picked.push(user);
    }
  });

  return picked;
}

/** Split total cents across N parts; sum(allocations) === totalCents exactly. */
export function splitCentsExactly(totalCents, count, seed) {
  if (count <= 0) {
    return [];
  }
  if (count === 1) {
    return [Math.max(0, Math.round(totalCents))];
  }

  const safeTotal = Math.max(0, Math.round(totalCents));
  const weights = [];
  for (let i = 0; i < count; i += 1) {
    weights.push(seededRandom(seed + i * 13) + 0.05);
  }
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);

  const allocations = new Array(count).fill(0);
  let remaining = safeTotal;

  for (let i = 0; i < count - 1; i += 1) {
    const slotsLeft = count - i;
    const maxForSlot = remaining - (slotsLeft - 1);
    let share = Math.floor((safeTotal * weights[i]) / weightSum);
    share = Math.min(Math.max(0, share), maxForSlot);
    allocations[i] = share;
    remaining -= share;
  }
  allocations[count - 1] = remaining;

  return allocations;
}

export function generateSubuserStatementsFromParent(parentStatement, parentReportId) {
  const activeSubUsers = getActiveSubUsers();
  if (!activeSubUsers.length) {
    return [];
  }

  const seed = Number(parentStatement?.id) || 1;
  const subUsers = pickSubusersForSplit(activeSubUsers, seed);
  const totalCents = Math.round((Number(parentStatement.price) || 0) * 100);
  const allocations = splitCentsExactly(totalCents, subUsers.length, seed);

  return subUsers.map((subUser, index) => {
    const rowId = buildSubuserStatementId(parentStatement.id, index);
    return {
      id: rowId,
      name: `Statement for ${subUser.friendly_name || subUser.username}`,
      kind: parentStatement.kind,
      sts_kind: parentStatement.sts_kind,
      quartal: parentStatement.quartal,
      month: parentStatement.month,
      year: parentStatement.year,
      year_period: parentStatement.year_period || "",
      issue_month: parentStatement.issue_month,
      issue_year: parentStatement.issue_year,
      short_description: parentStatement.short_description,
      price: (allocations[index] / 100).toFixed(2),
      currency: parentStatement.currency,
      status: parentStatement.status,
      new_details: parentStatement.new_details,
      new_overview: parentStatement.new_overview,
      invoice_generated: !!parentStatement.invoice_generated,
      created: parentStatement.created,
      sub_user: subUser.id,
      subuser_report__parent_report: parentReportId,
      visibility: rowId % 3 === 0 ? "active" : "inactive",
      subuser_statement_emails_sent: false,
    };
  });
}

function applyStatementOverridesToRows(rows) {
  const overrides = getSubuserStatementOverridesMap();
  return (rows || []).map(row => {
    const patch = overrides[String(row.id)];
    return patch ? { ...row, ...patch } : row;
  });
}

export function replaceGeneratedSubuserStatementsForReport(parentReportId, rows) {
  const existing = getGeneratedSubuserStatements();
  const filtered = existing.filter(
    row => String(row.subuser_report__parent_report || "") !== String(parentReportId),
  );
  setGeneratedSubuserStatements([...filtered, ...(rows || [])]);
}

export function getSubuserStatementRows() {
  ensureSeededSubuserAccounting();
  return applyStatementOverridesToRows(getGeneratedSubuserStatements());
}

export function findSubuserStatementRow(statementId) {
  return getSubuserStatementRows().find(
    row => String(row.id) === String(statementId),
  );
}

export function getSubuserAccountingStatus(statementId, parentReportId) {
  ensureSeededSubuserAccounting();
  const reports = getSubuserReportsMap();
  const stored = reports[String(statementId)];
  if (stored?.subuser_accounting_status) {
    return stored.subuser_accounting_status;
  }

  const hasGenerated = getGeneratedSubuserStatements().some(
    row => String(row.subuser_report__parent_report || "") === String(parentReportId),
  );
  return hasGenerated ? "completed" : "pending";
}

export function enrichStatementWithSubuserAccounting(row) {
  if (!row || Number(row.sts_kind) !== 2) {
    return row;
  }

  const parentReportId = buildUserReportItemId(row.id);
  return {
    ...row,
    user_report_item_id: parentReportId,
    subuser_accounting_status: getSubuserAccountingStatus(row.id, parentReportId),
  };
}

const SUBUSER_REPORT_QUEUED_DETAIL = "Subuser report generation has been queued. It may take from 15 minutes to 24 hours for the subuser accounting to be completed.";

export function queueSubuserReportForStatement(parentStatement) {
  if (!parentStatement?.id) {
    throw new Error("Statement not found.");
  }
  if (Number(parentStatement.sts_kind) !== 2) {
    throw new Error("Subuser accounting is only available for regular statements.");
  }

  const parentReportId = buildUserReportItemId(parentStatement.id);
  const currentStatus = getSubuserAccountingStatus(
    parentStatement.id,
    parentReportId,
  );

  if (currentStatus === "completed") {
    return {
      detail: "Subuser accounting is already available for this statement.",
    };
  }

  if (currentStatus === "in_progress") {
    return {
      detail: "Subuser report generation is already in progress.",
    };
  }

  upsertSubuserReport(parentStatement.id, {
    user_report_item_id: parentReportId,
    subuser_accounting_status: "in_progress",
  });

  const generated = generateSubuserStatementsFromParent(
    parentStatement,
    parentReportId,
  );

  if (!generated.length) {
    upsertSubuserReport(parentStatement.id, {
      user_report_item_id: parentReportId,
      subuser_accounting_status: "error",
    });
    throw new Error("No active sub-users are available to allocate earnings.");
  }

  replaceGeneratedSubuserStatementsForReport(parentReportId, generated);
  upsertSubuserReport(parentStatement.id, {
    user_report_item_id: parentReportId,
    subuser_accounting_status: "completed",
  });

  return {
    detail: SUBUSER_REPORT_QUEUED_DETAIL,
  };
}
